import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getCart, getViewHistory, removeFromCart } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Loader2, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function UserDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [cart, setCart] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      getCart(user.id),
      getViewHistory(user.id)
    ])
      .then(([cartData, historyData]) => {
        setCart(cartData || []);
        setHistory(historyData || []);
      })
      .catch((err) => toast.error("Failed to load dashboard data"))
      .finally(() => setLoading(false));
  }, [user]);

  const handleRemoveFromCart = async (productId) => {
    try {
      await removeFromCart(user.id, productId);
      setCart(cart.filter(item => item.product.id !== productId));
      toast.success("Item removed from cart");
    } catch (err) {
      toast.error("Failed to remove item");
    }
  };

  if (authLoading) return <div className="p-20 text-center font-display text-2xl">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-20" data-testid="user-dashboard">
      {/* Header section */}
      <section className="bg-black text-white border-b-[3px] border-black candy-dots">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="nb-pill inline-block mb-4 bg-white text-black">MY ACCOUNT</div>
          <h1 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight">
            Welcome Back!
          </h1>
          <p className="mt-3 text-lg font-medium text-white/80">
            {user.phone_number ? `Logged in with ${user.phone_number}` : "Your personal dashboard"}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 grid gap-16">
        
        {/* Cart Section */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display font-black uppercase text-3xl md:text-4xl">Your Cart</h2>
            <Link to="/shop" className="nb-btn bg-[var(--primary)] text-white text-sm px-4 py-2 flex items-center gap-2">
              Explore More <ArrowRight size={16} />
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center p-10"><Loader2 className="animate-spin text-[var(--primary)]" size={40} /></div>
          ) : cart.length === 0 ? (
            <div className="nb-card p-10 text-center bg-white">
              <h3 className="font-display font-bold text-xl mb-2">Your cart is empty</h3>
              <p className="text-gray-500 mb-6">Looks like you haven't added anything yet.</p>
              <Link to="/shop" className="nb-btn bg-black text-white inline-block">Start Shopping</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cart.map((item, i) => (
                <div key={item.id} className="relative group">
                  <ProductCard product={item.product} index={i} />
                  <button 
                    onClick={() => handleRemoveFromCart(item.product.id)}
                    className="absolute top-2 right-2 z-10 w-10 h-10 bg-red-500 text-white rounded-full border-[3px] border-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110 shadow-[2px_2px_0_0_#0A0A0A]"
                    title="Remove from cart"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recently Viewed Section */}
        <section>
          <div className="mb-8">
            <h2 className="font-display font-black uppercase text-3xl md:text-4xl">Recently Viewed</h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center p-10"><Loader2 className="animate-spin text-[var(--secondary)]" size={40} /></div>
          ) : history.length === 0 ? (
            <div className="nb-card p-10 text-center bg-white">
              <p className="text-gray-500">You haven't viewed any products recently.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {history.map((item, i) => (
                <ProductCard key={item.id} product={item.product} index={i} />
              ))}
            </div>
          )}
        </section>

      </div>
    </div>
  );
}
